import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  adoptLinkNote,
  adoptPasswordNote,
  buildPortalPublishPayload,
  portalAccessSentence,
  portalFieldsForSection,
  reachSentence,
  type PortalPublishFields,
} from '../../lib/portalLibrary/staffView';
import type { Account } from '../../types/account';
import type { HostedReportSummary } from '../../types/hostedReport';
import type { PortalAudienceReach } from '../../types/portalLibrary';
import { adoptToPortal } from './api';
import { audienceBlocker, useAudienceReach } from './AudienceEditor';
import { PortalPublishSection } from './PortalPublishSection';

// "Add to Kyck portal" for a report that is ALREADY hosted (Round 2c) — opened
// from the hosted-reports list row and from the Edit dialog.
//
// ⚠️⚠️ THE SAME FORM AS THE UPLOAD. The tile's fields are `PortalPublishSection`
// — the component the upload dialog renders — so the two ways of publishing
// cannot drift about what a tile needs. What is added here is only what is
// specific to an existing link:
//   - what publishing will DO to it (it takes the tile's end date — one date,
//     not two — so an expired link comes back, a live one lives longer or stops
//     earlier), said BEFORE the click;
//   - an explicit "Remove the password" tick when it has one. Never automatic.
// The server re-checks all of it (revoked / already linked / password / reach).

export function AddToPortalDialog({
  report,
  accounts,
  onClose,
}: {
  report: HostedReportSummary;
  accounts: Account[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(report.title);
  const [portal, setPortal] = useState<PortalPublishFields>(() =>
    portalFieldsForSection('college', Date.now()),
  );
  const [clearPassword, setClearPassword] = useState(false);
  const [done, setDone] = useState<PortalAudienceReach | null>(null);
  const reach = useAudienceReach(portal.audience, portal.section);

  const payload = buildPortalPublishPayload(portal);
  const linkNote = payload.ok
    ? adoptLinkNote(report.expiresAt, payload.payload.visibleUntilMs, Date.now())
    : null;
  const blocker =
    title.trim().length === 0
      ? 'Give it a title.'
      : !payload.ok
        ? payload.message
        : audienceBlocker(portal.audience, reach);

  const mutation = useMutation({
    mutationFn: () => {
      if (!payload.ok) throw new Error(payload.message);
      return adoptToPortal({
        ...payload.payload,
        title: title.trim(),
        hostedReportId: report.id,
        clearPassword,
      });
    },
    onSuccess: (res) => {
      setDone(res.reach);
      void queryClient.invalidateQueries({ queryKey: ['hosted-reports'] });
      void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
    },
  });

  if (done) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Added to the Kyck portal</DialogTitle>
            <DialogDescription>
              {reachSentence(done).replace('will appear', 'appears')} {portalAccessSentence(done)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const busy = mutation.isPending;
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add to Kyck portal</DialogTitle>
          <DialogDescription>
            Publish “{report.title}” as a tile in the client portal. The link stays the same.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="atp-title">Tile title</Label>
            <Input id="atp-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <PortalPublishSection value={portal} onChange={setPortal} accounts={accounts} />

          {linkNote && (
            <p
              className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
              data-testid="adopt-link-note"
            >
              {linkNote}
            </p>
          )}

          {report.hasPassword && (
            <div className="space-y-1 rounded-md border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <Checkbox
                  checked={clearPassword}
                  onCheckedChange={(v) => setClearPassword(v === true)}
                  aria-label="Remove the password"
                />
                Remove the password (optional)
              </label>
              <p className="text-xs text-slate-500">{adoptPasswordNote(clearPassword)}</p>
            </div>
          )}

          {blocker && <p className="text-xs text-slate-500">{blocker}</p>}
          {mutation.error && (
            <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={busy || blocker !== null} onClick={() => mutation.mutate()}>
            {busy ? 'Adding…' : 'Add to portal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
